import { putLocal, enqueue } from './localStore';

// Single abstraction used by components/hooks to create sections/rooms/
// equipments+usages: same call shape whether online or offline, only the
// `offline` flag on the return value differs (used for toast copy, not
// control flow — the caller's optimistic local-state update is identical
// either way).

async function postJson(url, body) {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`Erreur lors de l'enregistrement (${url}): ${response.status}`);
    }
    return response;
}

export async function createSection(sectionData, { isOnline }) {
    if (isOnline) {
        await postJson('/load_new_section', sectionData);
        await putLocal('sections', sectionData, 'synced');
        return { ok: true, offline: false };
    }

    await putLocal('sections', sectionData, 'pending');
    await enqueue({
        entityType: 'section',
        entityId: sectionData._id,
        method: 'POST',
        url: '/load_new_section',
        body: sectionData,
    });
    return { ok: true, offline: true };
}

export async function createRoom(roomData, { isOnline }) {
    if (isOnline) {
        await postJson('/load_new_room', roomData);
        await putLocal('rooms', roomData, 'synced');
        return { ok: true, offline: false };
    }

    await putLocal('rooms', roomData, 'pending');
    await enqueue({
        entityType: 'room',
        entityId: roomData._id,
        method: 'POST',
        url: '/load_new_room',
        body: roomData,
        dependsOnEntityId: roomData.id_section,
    });
    return { ok: true, offline: true };
}

// Queues a photo for deferred upload once an equipment was created offline.
// dependsOnEntityId ties it to the equipment's own queue entry so a
// permanently-failed equipment creation (4xx) blocks the photo too, via the
// same dependency check drainQueue already runs for rooms/sections.
export async function enqueuePhotoUpload(equipmentId, blob, fileName = 'equipment-photo.jpg') {
    return enqueue({
        entityType: 'photo',
        entityId: equipmentId,
        kind: 'photo',
        blob,
        fileName,
        dependsOnEntityId: equipmentId,
    });
}

export async function createEquipmentWithUsages(equipmentData, usages, { isOnline }) {
    if (isOnline) {
        await postJson('/load_new_equipment', equipmentData);
        if (usages.length) {
            await postJson('/load_new_usages', { usages });
        }
        await putLocal('equipments', equipmentData, 'synced');
        await Promise.all(usages.map((usage) => putLocal('usages', usage, 'synced')));
        return { ok: true, offline: false };
    }

    await putLocal('equipments', equipmentData, 'pending');
    await enqueue({
        entityType: 'equipment',
        entityId: equipmentData._id,
        method: 'POST',
        url: '/load_new_equipment',
        body: equipmentData,
        dependsOnEntityId: equipmentData.location?.room_id,
    });

    if (usages.length) {
        await Promise.all(usages.map((usage) => putLocal('usages', usage, 'pending')));
        await enqueue({
            entityType: 'usages',
            entityId: equipmentData._id,
            method: 'POST',
            url: '/load_new_usages',
            body: { usages },
            dependsOnEntityId: equipmentData._id,
        });
    }

    return { ok: true, offline: true };
}
